/**
 * iOS Compatibility Utilities
 * 
 * Detects iOS/Safari, provides camera fallbacks, and auth persistence helpers.
 * Critical for fixing iPhone-specific issues with camera, session, and API calls.
 */

// ═══════════════════════════════════════════════════════════════════════════════
// DEVICE DETECTION
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Detect if running on iOS (iPhone/iPad)
 */
export const isIOS = () => {
  if (typeof navigator === 'undefined') return false;
  
  const ua = navigator.userAgent || '';
  const platform = navigator.platform || '';
  
  // Check for iOS indicators
  const isIOSUA = /iPad|iPhone|iPod/.test(ua);
  const isIOSPlatform = /iPad|iPhone|iPod/.test(platform);
  
  // iPad with iPadOS 13+ reports as MacIntel but has touch
  const isIPadOS13 = platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  
  return isIOSUA || isIOSPlatform || isIPadOS13;
};

/**
 * Detect Safari browser (including iOS Safari)
 */
export const isSafari = () => {
  if (typeof navigator === 'undefined') return false;
  
  const ua = navigator.userAgent || '';
  
  // Safari but not Chrome/Firefox/Edge (which include "Safari" in UA)
  const isSafariBrowser = /^((?!chrome|android|crios|fxios|edgios).)*safari/i.test(ua);
  
  // iOS always uses WebKit (even Chrome on iOS is WebKit)
  const isWebKitOnIOS = isIOS() && /WebKit/i.test(ua);
  
  return isSafariBrowser || isWebKitOnIOS;
};

/**
 * Get detailed device/browser info for diagnostics
 */
export const getDeviceInfo = () => {
  if (typeof navigator === 'undefined') {
    return {
      userAgent: 'server-side',
      isIOS: false,
      isSafari: false,
      isSecureContext: false,
      hasMediaDevices: false,
      hasGetUserMedia: false,
      platform: 'unknown'
    };
  }
  
  return {
    userAgent: navigator.userAgent,
    isIOS: isIOS(),
    isSafari: isSafari(),
    isSecureContext: window.isSecureContext || false,
    hasMediaDevices: !!navigator.mediaDevices,
    hasGetUserMedia: !!(navigator.mediaDevices?.getUserMedia),
    platform: navigator.platform || 'unknown',
    maxTouchPoints: navigator.maxTouchPoints || 0,
    standalone: window.navigator.standalone || false, // PWA mode on iOS
    cookiesEnabled: navigator.cookieEnabled || false
  };
};

// ═══════════════════════════════════════════════════════════════════════════════
// AUTH PERSISTENCE HELPERS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Check if localStorage is available and working
 * iOS Safari in private mode throws on setItem
 */
export const isLocalStorageAvailable = () => {
  try {
    const testKey = '__ls_test__';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Safe localStorage getter with fallback
 */
export const safeGetItem = (key, fallback = null) => {
  try {
    if (!isLocalStorageAvailable()) return fallback;
    const value = localStorage.getItem(key);
    return value !== null ? value : fallback;
  } catch (e) {
    console.warn('[Storage] Failed to get item:', key, e);
    return fallback;
  }
};

/**
 * Safe localStorage setter with fallback
 */
export const safeSetItem = (key, value) => {
  try {
    if (!isLocalStorageAvailable()) {
      console.warn('[Storage] localStorage not available, value not persisted');
      return false;
    }
    localStorage.setItem(key, value);
    return true;
  } catch (e) {
    console.warn('[Storage] Failed to set item:', key, e);
    return false;
  }
};

/**
 * Safe localStorage remover
 */
export const safeRemoveItem = (key) => {
  try {
    if (!isLocalStorageAvailable()) return false;
    localStorage.removeItem(key);
    return true;
  } catch (e) {
    console.warn('[Storage] Failed to remove item:', key, e);
    return false;
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// CAMERA UTILITIES FOR iOS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Check if we should use file input instead of getUserMedia
 * Returns true for iOS Safari where file input is more reliable
 */
export const shouldUseFileInputForCamera = () => {
  // Always prefer file input on iOS for reliability
  if (isIOS()) return true;
  
  // If not in secure context, file input is the only option
  if (typeof window !== 'undefined' && !window.isSecureContext) return true;
  
  // If getUserMedia is not available, use file input
  if (!navigator.mediaDevices?.getUserMedia) return true;
  
  return false;
};

/**
 * iOS-friendly camera error handler
 */
export const handleCameraError = (error) => {
  const errorName = error?.name || 'UnknownError';
  const errorMessage = error?.message || 'Unknown camera error';
  
  const errorInfo = {
    name: errorName,
    message: errorMessage,
    isIOS: isIOS(),
    timestamp: new Date().toISOString()
  };
  
  // Map common errors to user-friendly messages
  let userMessage = 'Camera error. Please try using "Choose from Photos" instead.';
  let recoveryAction = 'use_file_input';
  
  switch (errorName) {
    case 'NotAllowedError':
      userMessage = 'Camera permission denied. Please allow camera access in your browser settings.';
      recoveryAction = 'request_permission';
      break;
    case 'NotFoundError':
      userMessage = 'No camera found. Please use "Choose from Photos" to upload an image.';
      recoveryAction = 'use_file_input';
      break;
    case 'NotReadableError':
      userMessage = 'Camera is busy or unavailable. Please close other apps using the camera.';
      recoveryAction = 'retry_or_file_input';
      break;
    case 'AbortError':
      userMessage = 'Camera was interrupted. Please try again.';
      recoveryAction = 'retry';
      break;
    case 'OverconstrainedError':
      userMessage = 'Camera settings not supported. Trying alternative...';
      recoveryAction = 'retry_with_fallback';
      break;
    case 'SecurityError':
      userMessage = 'Camera blocked due to security settings. Please use HTTPS.';
      recoveryAction = 'use_file_input';
      break;
    default:
      // Generic fallback
      break;
  }
  
  return {
    ...errorInfo,
    userMessage,
    recoveryAction
  };
};

// ═══════════════════════════════════════════════════════════════════════════════
// API DIAGNOSTICS LOGGING
// ═══════════════════════════════════════════════════════════════════════════════

// In-memory log for diagnostics (max 20 entries)
let apiEventLog = [];
const MAX_LOG_ENTRIES = 20;

/**
 * Log an API event for diagnostics
 */
export const logApiEvent = (event) => {
  const entry = {
    timestamp: new Date().toISOString(),
    ...event
  };
  
  apiEventLog.unshift(entry);
  
  // Keep only last 20 entries
  if (apiEventLog.length > MAX_LOG_ENTRIES) {
    apiEventLog = apiEventLog.slice(0, MAX_LOG_ENTRIES);
  }
  
  // Also log to console in dev
  if (process.env.NODE_ENV === 'development' || event.status >= 400) {
    console.log('[API]', event.method, event.endpoint, event.status, event.error || '');
  }
};

/**
 * Get API event log for diagnostics
 */
export const getApiEventLog = () => [...apiEventLog];

/**
 * Clear API event log
 */
export const clearApiEventLog = () => {
  apiEventLog = [];
};

// ═══════════════════════════════════════════════════════════════════════════════
// CAMERA DIAGNOSTICS
// ═══════════════════════════════════════════════════════════════════════════════

let cameraState = {
  lastMethod: null, // 'file_input' | 'getUserMedia'
  lastError: null,
  permissionStatus: 'unknown', // 'unknown' | 'granted' | 'denied' | 'prompt'
  lastAttempt: null
};

/**
 * Update camera state for diagnostics
 */
export const updateCameraState = (update) => {
  cameraState = {
    ...cameraState,
    ...update,
    lastAttempt: new Date().toISOString()
  };
};

/**
 * Get camera state for diagnostics
 */
export const getCameraState = () => ({ ...cameraState });

// ═══════════════════════════════════════════════════════════════════════════════
// AUTH STATE TRACKING
// ═══════════════════════════════════════════════════════════════════════════════

let authState = {
  mode: 'token', // 'token' | 'cookie'
  isReady: false,
  lastCheck: null,
  userId: null,
  email: null,
  error: null
};

/**
 * Update auth state for diagnostics
 */
export const updateAuthState = (update) => {
  authState = {
    ...authState,
    ...update,
    lastCheck: new Date().toISOString()
  };
};

/**
 * Get auth state for diagnostics
 */
export const getAuthState = () => ({ ...authState });

// ═══════════════════════════════════════════════════════════════════════════════
// ENTITLEMENTS STATE TRACKING
// ═══════════════════════════════════════════════════════════════════════════════

let entitlementsState = {
  loaded: false,
  tier: null,
  tierName: null,
  status: null,
  trialDaysRemaining: null,
  isTester: false,
  lastFetch: null,
  error: null
};

/**
 * Update entitlements state for diagnostics
 */
export const updateEntitlementsState = (update) => {
  entitlementsState = {
    ...entitlementsState,
    ...update,
    lastFetch: new Date().toISOString()
  };
};

/**
 * Get entitlements state for diagnostics
 */
export const getEntitlementsState = () => ({ ...entitlementsState });

// ═══════════════════════════════════════════════════════════════════════════════
// FULL DIAGNOSTICS EXPORT
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Get full diagnostics snapshot for tester panel
 */
export const getDiagnosticsSnapshot = () => {
  return {
    device: getDeviceInfo(),
    auth: getAuthState(),
    entitlements: getEntitlementsState(),
    camera: getCameraState(),
    apiEvents: getApiEventLog(),
    storage: {
      available: isLocalStorageAvailable(),
      sessionToken: safeGetItem('session_token') ? 'present' : 'missing',
      userId: safeGetItem('calorie_tracker_user_id') || 'missing'
    },
    timestamp: new Date().toISOString()
  };
};

export default {
  isIOS,
  isSafari,
  getDeviceInfo,
  isLocalStorageAvailable,
  safeGetItem,
  safeSetItem,
  safeRemoveItem,
  shouldUseFileInputForCamera,
  handleCameraError,
  logApiEvent,
  getApiEventLog,
  updateCameraState,
  getCameraState,
  updateAuthState,
  getAuthState,
  updateEntitlementsState,
  getEntitlementsState,
  getDiagnosticsSnapshot
};
